/**
 * SatisfactionPage - Page principale Satisfaction & Insertion
 * Refactorisée pour utiliser des composants modulaires
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Send, RefreshCw, Building, Download, FileText, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Import des composants refactorisés
import {
  SatisfactionKPIs,
  GlobalRating,
  DashboardTab,
  QuestionnairesTab,
  InsertionTab,
  DiplomesTab,
  SendQuestionnaireDialog,
  CreateQuestionnaireDialog,
  ResponsesDialog,
  SaveModeleDialog,
  QUESTIONS_PAR_DEFAUT
} from './satisfaction';

const SatisfactionPage = ({ user }) => {
  // États principaux
  const [stats, setStats] = useState(null);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [reponses, setReponses] = useState([]);
  const [types, setTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [centreFilter, setCentreFilter] = useState('all');
  
  // États des dialogues
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showReponsesDialog, setShowReponsesDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [sending, setSending] = useState(false);
  
  // Destinataires
  const [destinataires, setDestinataires] = useState({
    stagiaires: [],
    formateurs: [],
    entreprises: [],
    tuteurs: [],
    moniteurs: [],
    financeurs: []
  });
  const [selectedDestinataires, setSelectedDestinataires] = useState([]);
  const [destinataireFilter, setDestinataireFilter] = useState('stagiaires');
  
  // Questions personnalisées
  const [customQuestions, setCustomQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState({ texte: '', type: 'rating' });
  const [savedModeles, setSavedModeles] = useState({});
  
  // Insertion par période
  const [insertionParPeriode, setInsertionParPeriode] = useState([]);
  
  // Données d'envoi
  const [sendData, setSendData] = useState({
    type: 'stagiaire_diplome',
    destinataire_email: '',
    destinataire_nom: '',
    destinataire_id: '',
    financeur_adresse: ''
  });
  
  // Modèles personnalisés
  const [modelesPersonnalises, setModelesPersonnalises] = useState([]);
  const [showSaveModeleDialog, setShowSaveModeleDialog] = useState(false);
  const [newModeleName, setNewModeleName] = useState('');

  // Chargement des données
  useEffect(() => {
    loadData();
    loadDestinataires();
    loadInsertionParPeriode();
    loadModelesPersonnalises();
  }, [centreFilter]);

  const loadDestinataires = async () => {
    try {
      const response = await axios.get('/questionnaires/destinataires');
      const data = response.data;
      // Grouper par type
      const grouped = {
        stagiaires: data.filter(d => d.type === 'stagiaire'),
        formateurs: data.filter(d => d.type === 'formateur'),
        entreprises: data.filter(d => d.type === 'entreprise'),
        tuteurs: data.filter(d => d.type === 'tuteur'),
        moniteurs: data.filter(d => d.type === 'moniteur_vnm'),
        financeurs: []
      };
      setDestinataires(grouped);
    } catch (error) {
    }
  };

  const loadInsertionParPeriode = async () => {
    try {
      const response = await axios.get('/moniteurs/insertion-par-periode');
      setInsertionParPeriode(response.data);
    } catch (error) {
    }
  };

  const loadModelesPersonnalises = async () => {
    try {
      const response = await axios.get('/questionnaires/templates');
      const modeles = response.data.map(t => ({
        id: t.template_id,
        name: t.name,
        questions: t.questions
      }));
      setModelesPersonnalises(modeles);
    } catch (error) {
    }
  };

  const loadData = async () => {
    try {
      const params = centreFilter !== 'all' ? `?centre=${centreFilter}` : '';
      const [statsRes, questionnairesRes, typesRes] = await Promise.all([
        axios.get(`/satisfaction/stats${params}`),
        axios.get(`/questionnaires${params}`),
        axios.get('/questionnaires/types')
      ]);
      setStats(statsRes.data);
      setQuestionnaires(questionnairesRes.data);
      setTypes(typesRes.data);
      
      const reponsesCompletes = questionnairesRes.data.filter(q => q.statut === 'complete');
      setReponses(reponsesCompletes);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Actions sur les questionnaires
  const sendQuestionnaire = async () => {
    if (!sendData.destinataire_email || !sendData.destinataire_nom) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (sendData.type === 'financeur' && !sendData.financeur_adresse) {
      toast.error('Veuillez renseigner l\'adresse du financeur');
      return;
    }
    
    setSending(true);
    try {
      await axios.post('/questionnaires/send', sendData);
      toast.success('Questionnaire envoyé !');
      setShowSendDialog(false);
      loadData();
      setSendData({
        type: 'stagiaire_diplome',
        destinataire_email: '',
        destinataire_nom: '',
        destinataire_id: '',
        financeur_adresse: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const deleteQuestionnaire = async (questionnaireId) => {
    if (!window.confirm('Supprimer ce questionnaire et ses réponses ?')) return;
    
    try {
      await axios.delete(`/questionnaires/${questionnaireId}`);
      toast.success('Questionnaire supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const sendMultipleQuestionnaires = async () => {
    if (selectedDestinataires.length === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire');
      return;
    }
    
    setSending(true);
    try {
      const payload = {
        type: sendData.type,
        destinataires: selectedDestinataires,
        questions: customQuestions.length > 0 ? customQuestions : null
      };
      
      const response = await axios.post('/questionnaires/send-multiple', payload);
      toast.success(`${response.data.sent} questionnaire(s) envoyé(s)`);
      setShowCreateDialog(false);
      setSelectedDestinataires([]);
      setCustomQuestions([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const sendRelance = async (questionnaireId) => {
    try {
      const res = await axios.post(`/questionnaires/${questionnaireId}/relance`);
      toast.success(res.data.email_sent ? 'Relance envoyée par email' : 'Relance enregistrée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const autoRelance = async () => {
    if (!window.confirm('Envoyer un email de relance à tous les questionnaires sans réponse depuis plus de 7 jours ?')) return;
    try {
      const res = await axios.post('/questionnaires/auto-relance');
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors des relances');
    }
  };

  // Gestion des destinataires
  const toggleDestinataire = (dest) => {
    setSelectedDestinataires(prev => {
      const exists = prev.find(d => d.email === dest.email);
      if (exists) {
        return prev.filter(d => d.email !== dest.email);
      } else {
        return [...prev, dest];
      }
    });
  };

  const selectAllDestinataires = () => {
    const currentList = destinataires[destinataireFilter] || [];
    setSelectedDestinataires(currentList);
  };

  const clearSelectedDestinataires = () => {
    setSelectedDestinataires([]);
  };

  // Gestion des questions personnalisées
  const addCustomQuestion = () => {
    if (!newQuestion.texte.trim()) {
      toast.error('Veuillez saisir le texte de la question');
      return;
    }
    setCustomQuestions(prev => [
      ...prev, 
      { id: `q${prev.length + 1}`, texte: newQuestion.texte, type: newQuestion.type }
    ]);
    setNewQuestion({ texte: '', type: 'rating' });
  };

  const removeCustomQuestion = (index) => {
    setCustomQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const handleTypeChange = (newType) => {
    if (customQuestions.length > 0) {
      setSavedModeles(prev => ({
        ...prev,
        [sendData.type]: customQuestions
      }));
    }
    
    if (savedModeles[newType]) {
      setCustomQuestions(savedModeles[newType]);
    } else {
      setCustomQuestions([]);
    }
    
    setSendData({...sendData, type: newType});
  };

  const resetToDefault = () => {
    const defaultQ = QUESTIONS_PAR_DEFAUT[sendData.type] || [];
    setCustomQuestions([...defaultQ]);
    toast.info('Questions réinitialisées par défaut');
  };

  // Gestion des modèles
  const saveModele = () => {
    if (customQuestions.length === 0) {
      toast.error('Aucune question à sauvegarder');
      return;
    }
    setShowSaveModeleDialog(true);
  };

  const confirmSaveModele = async () => {
    if (!newModeleName.trim()) {
      toast.error('Veuillez donner un nom au modèle');
      return;
    }
    
    try {
      const response = await axios.post('/questionnaires/templates', {
        name: newModeleName.trim(),
        questions: [...customQuestions]
      });
      
      const newModele = {
        id: response.data.template_id,
        name: newModeleName.trim(),
        questions: [...customQuestions]
      };
      
      setModelesPersonnalises(prev => [...prev, newModele]);
      setShowSaveModeleDialog(false);
      setNewModeleName('');
      toast.success(`Modèle "${newModele.name}" créé et sauvegardé !`);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de la sauvegarde du modèle');
      }
    }
  };

  const deleteModelePersonnalise = async (modeleId) => {
    try {
      await axios.delete(`/questionnaires/templates/${modeleId}`);
      setModelesPersonnalises(prev => prev.filter(m => m.id !== modeleId));
      toast.success('Modèle supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression du modèle');
    }
  };

  const loadModelePersonnalise = (modele) => {
    setCustomQuestions([...modele.questions]);
    toast.info(`Modèle "${modele.name}" chargé`);
  };

  const viewReponses = (questionnaire) => {
    setSelectedQuestionnaire(questionnaire);
    setShowReponsesDialog(true);
  };

  // Export des données
  const exportData = async (format) => {
    try {
      if (format === 'pdf') {
        const jsPDFModule = await import('jspdf');
        const autoTableModule = await import('jspdf-autotable');
        const jsPDF = jsPDFModule.jsPDF || jsPDFModule.default;
        const autoTable = autoTableModule.default;
        const doc = new jsPDF();
        const pw = doc.internal.pageSize.getWidth();

        doc.setFontSize(18);
        doc.setTextColor(15, 23, 42);
        doc.text('Rapport Satisfaction & Insertion', pw / 2, 20, { align: 'center' });
        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139);
        doc.text(`FISL - Centre de Formation Motonautisme | ${new Date().toLocaleDateString('fr-FR')}`, pw / 2, 28, { align: 'center' });

        let y = 38;

        // Stats globales
        doc.setFontSize(13);
        doc.setTextColor(15, 23, 42);
        doc.text('Statistiques globales', 14, y);
        y += 8;
        const globalData = [
          ['Taux de réponse', `${stats?.taux_reponse || 0}%`],
          ['Questionnaires envoyés', `${stats?.total_envoyes || 0}`],
          ['Questionnaires complétés', `${stats?.total_completes || 0}`],
          ['Moyenne globale', moyenneGlobale ? `${moyenneGlobale}/5` : 'N/A'],
        ];
        autoTable(doc, { startY: y, body: globalData, theme: 'plain', styles: { fontSize: 9 }, margin: { left: 14, right: 14 } });
        y = doc.lastAutoTable.finalY + 10;

        // Questionnaires
        if (questionnaires.length > 0) {
          doc.setFontSize(13);
          doc.setTextColor(15, 23, 42);
          doc.text('Questionnaires', 14, y);
          y += 6;
          const qData = questionnaires.map(q => [
            q.destinataire_nom || '-',
            types[q.type] || q.type || '-',
            q.statut === 'complete' ? 'Complété' : q.statut === 'envoye' ? 'Envoyé' : q.statut,
            q.date_envoi ? new Date(q.date_envoi).toLocaleDateString('fr-FR') : '-'
          ]);
          autoTable(doc, {
            startY: y, head: [['Destinataire', 'Type', 'Statut', 'Date']], body: qData,
            theme: 'striped', headStyles: { fillColor: [141, 198, 63], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8 }, margin: { left: 14, right: 14 }
          });
          y = doc.lastAutoTable.finalY + 10;
        }

        // Insertion par période
        if (insertionParPeriode.length > 0) {
          if (y > 230) { doc.addPage(); y = 20; }
          doc.setFontSize(13);
          doc.setTextColor(15, 23, 42);
          doc.text('Insertion professionnelle', 14, y);
          y += 6;
          const iData = insertionParPeriode.map(p => [
            p.periode, `${p.total}`, `${p.taux_insertion}%`,
            `${p.stats?.en_emploi || 0}`, `${p.stats?.en_recherche || 0}`, `${p.stats?.arret || 0}`
          ]);
          autoTable(doc, {
            startY: y, head: [['Période', 'Total', 'Taux insertion', 'En emploi', 'En recherche', 'Arrêt']], body: iData,
            theme: 'striped', headStyles: { fillColor: [59, 130, 246], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8 }, margin: { left: 14, right: 14 }
          });
        }

        doc.save(`${new Date().getFullYear()}_rapport_satisfaction.pdf`);
        toast.success('Rapport PDF téléchargé');
      } else if (format === 'xls') {
        let xlsContent = 'RAPPORT SATISFACTION & INSERTION - FISL\n\n';
        
        // Stats globales
        xlsContent += 'STATISTIQUES GLOBALES\n';
        xlsContent += `Taux de réponse\t${stats?.taux_reponse || 0}%\n`;
        xlsContent += `Questionnaires envoyés\t${stats?.total_envoyes || 0}\n`;
        xlsContent += `Questionnaires complétés\t${stats?.total_completes || 0}\n`;
        xlsContent += `Moyenne globale\t${moyenneGlobale || 'N/A'}\n\n`;

        // Questionnaires
        xlsContent += 'QUESTIONNAIRES\n';
        xlsContent += 'Destinataire\tType\tStatut\tDate envoi\n';
        questionnaires.forEach(q => {
          xlsContent += `${q.destinataire_nom || '-'}\t${types[q.type] || q.type || '-'}\t${q.statut || '-'}\t${q.date_envoi ? new Date(q.date_envoi).toLocaleDateString('fr-FR') : '-'}\n`;
        });
        xlsContent += '\n';

        // Insertion par période
        if (insertionParPeriode.length > 0) {
          xlsContent += 'INSERTION PROFESSIONNELLE\n';
          insertionParPeriode.forEach(periode => {
            xlsContent += `\n=== ${periode.periode} après diplôme ===\n`;
            xlsContent += `Total moniteurs\t${periode.total}\n`;
            xlsContent += `Taux d'insertion\t${periode.taux_insertion}%\n`;
            xlsContent += `En emploi\t${periode.stats?.en_emploi || 0}\n`;
            xlsContent += `En recherche\t${periode.stats?.en_recherche || 0}\n\n`;
            xlsContent += 'Nom\tEmail\tStatut\tType emploi\tEmployeur\tAnnée diplôme\n';
            periode.moniteurs?.forEach(m => {
              xlsContent += `${m.nom}\t${m.email || ''}\t${m.statut || ''}\t${m.type_emploi || ''}\t${m.employeur || ''}\t${m.annee_diplome || ''}\n`;
            });
          });
        }
        
        const blob = new Blob(['\uFEFF' + xlsContent], { type: 'application/vnd.ms-excel;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${new Date().getFullYear()}_rapport_satisfaction.xls`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Données exportées en XLS');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // Calcul de la moyenne globale
  const calculerMoyenneGlobale = () => {
    const reponsesAvecNotes = reponses.filter(r => r.reponses);
    if (reponsesAvecNotes.length === 0) return null;
    
    let totalNotes = 0;
    let countNotes = 0;
    
    reponsesAvecNotes.forEach(r => {
      if (r.reponses) {
        Object.values(r.reponses).forEach(val => {
          if (typeof val === 'number' && val >= 1 && val <= 5) {
            totalNotes += val;
            countNotes++;
          }
        });
      }
    });
    
    return countNotes > 0 ? (totalNotes / countNotes).toFixed(1) : null;
  };

  const moyenneGlobale = calculerMoyenneGlobale();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[#8DC63F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="satisfaction-page">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Satisfaction & Insertion</h1>
          <p className="text-slate-500">Suivi Qualiopi et indicateurs clés</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={centreFilter} onValueChange={setCentreFilter}>
            <SelectTrigger className="w-[180px]" data-testid="filter-centre">
              <Building className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les centres</SelectItem>
              <SelectItem value="atlantique">Centre Atlantique</SelectItem>
              <SelectItem value="occitanie">Centre Occitanie</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => exportData('pdf')} data-testid="export-satisfaction-pdf-btn">
            <FileText className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button variant="outline" onClick={() => exportData('xls')}>
            <Download className="h-4 w-4 mr-2" />
            Export XLS
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Créer questionnaire
          </Button>
          <Button onClick={() => setShowSendDialog(true)} className="bg-[#8DC63F] hover:bg-[#7AB62F]">
            <Send className="h-4 w-4 mr-2" />
            Envoi rapide
          </Button>
        </div>
      </div>

      {/* Moyenne globale */}
      <GlobalRating 
        moyenneGlobale={moyenneGlobale} 
        reponses={reponses} 
        centreFilter={centreFilter}
        moyennesParType={stats?.moyennes}
        types={types}
      />

      {/* KPIs */}
      <SatisfactionKPIs stats={stats} />

      {/* Onglets */}
      <Tabs defaultValue="dashboard">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="dashboard" className="text-xs sm:text-sm">Tableau de bord</TabsTrigger>
          <TabsTrigger value="questionnaires" className="text-xs sm:text-sm">Questionnaires ({questionnaires.length})</TabsTrigger>
          <TabsTrigger value="insertion" className="text-xs sm:text-sm">Insertion ({insertionParPeriode.reduce((sum, p) => sum + p.total, 0) || 0})</TabsTrigger>
          <TabsTrigger value="diplomes" className="text-xs sm:text-sm">Diplômés</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <DashboardTab stats={stats} types={types} />
        </TabsContent>

        <TabsContent value="questionnaires">
          <QuestionnairesTab 
            questionnaires={questionnaires}
            types={types}
            onSendClick={() => setShowSendDialog(true)}
            onViewReponses={viewReponses}
            onRelance={sendRelance}
            onDelete={deleteQuestionnaire}
            onAutoRelance={autoRelance}
          />
        </TabsContent>

        <TabsContent value="insertion">
          <InsertionTab 
            stats={stats} 
            insertionParPeriode={insertionParPeriode} 
          />
        </TabsContent>

        <TabsContent value="diplomes">
          <DiplomesTab />
        </TabsContent>
      </Tabs>

      {/* Dialogues */}
      <SendQuestionnaireDialog
        open={showSendDialog}
        onOpenChange={setShowSendDialog}
        types={types}
        destinataires={destinataires}
        sendData={sendData}
        setSendData={setSendData}
        onSend={sendQuestionnaire}
        sending={sending}
      />

      <ResponsesDialog
        open={showReponsesDialog}
        onOpenChange={setShowReponsesDialog}
        questionnaire={selectedQuestionnaire}
        types={types}
      />

      <CreateQuestionnaireDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        sendData={sendData}
        handleTypeChange={handleTypeChange}
        modelesPersonnalises={modelesPersonnalises}
        loadModelePersonnalise={loadModelePersonnalise}
        deleteModelePersonnalise={deleteModelePersonnalise}
        customQuestions={customQuestions}
        setCustomQuestions={setCustomQuestions}
        newQuestion={newQuestion}
        setNewQuestion={setNewQuestion}
        addCustomQuestion={addCustomQuestion}
        removeCustomQuestion={removeCustomQuestion}
        savedModeles={savedModeles}
        resetToDefault={resetToDefault}
        saveModele={saveModele}
        destinataireFilter={destinataireFilter}
        setDestinataireFilter={setDestinataireFilter}
        destinataires={destinataires}
        selectedDestinataires={selectedDestinataires}
        toggleDestinataire={toggleDestinataire}
        selectAllDestinataires={selectAllDestinataires}
        clearSelectedDestinataires={clearSelectedDestinataires}
        sending={sending}
        onSendMultiple={sendMultipleQuestionnaires}
        onClose={() => {
          setShowCreateDialog(false);
          setSelectedDestinataires([]);
          setCustomQuestions([]);
        }}
      />

      <SaveModeleDialog
        open={showSaveModeleDialog}
        onOpenChange={setShowSaveModeleDialog}
        modeleName={newModeleName}
        setModeleName={setNewModeleName}
        customQuestions={customQuestions}
        onConfirm={confirmSaveModele}
      />
    </div>
  );
};

export default SatisfactionPage;
